import {
  STUDIO_FEATURED_OUTCOME_SOURCE,
  parseStudioOutcomeIntent,
  type StudioOutcomeIntent,
  type StudioOutcomeSkuId,
} from "./studio-outcome-catalog";

export const STUDIO_FEATURED_OUTCOME_DRAFT_VERSION =
  "cogo.studio-featured-outcome-draft.v3" as const;

const STORAGE_KEY = "cogo.studio.featured-outcome-draft.v3";
const LEGACY_STORAGE_KEYS = [
  "cogo.studio.featured-outcome-draft.v1",
  "cogo.studio.featured-outcome-draft.v2",
] as const;
const PROMPT_LIMIT = 12_000;
const PRINCIPAL_ID_LIMIT = 256;
const CONTROL_CHARACTER_PATTERN = /[\u0000-\u001f\u007f-\u009f]/;
const DRAFT_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
let activeGenerationRequestId: string | null = null;

export interface StudioFeaturedOutcomeDraft {
  schemaVersion: typeof STUDIO_FEATURED_OUTCOME_DRAFT_VERSION;
  outcomeIntent: StudioOutcomeIntent;
  draftId: string;
  principalId: string | null;
  prompt: string;
  awaitingClarification: boolean;
}

function sessionStorageOrNull(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function validPrompt(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.trim().length > 0 &&
    value.length <= PROMPT_LIMIT &&
    !CONTROL_CHARACTER_PATTERN.test(value)
  );
}

function validPrincipalId(value: unknown): value is string | null {
  return (
    value === null ||
    (typeof value === "string" &&
      value.trim().length > 0 &&
      value.length <= PRINCIPAL_ID_LIMIT &&
      !CONTROL_CHARACTER_PATTERN.test(value))
  );
}

function validDraftId(value: unknown): value is string {
  return typeof value === "string" && DRAFT_ID_PATTERN.test(value);
}

function removeStoredDraft(storage: Storage): void {
  try {
    storage.removeItem(STORAGE_KEY);
    for (const key of LEGACY_STORAGE_KEYS) storage.removeItem(key);
  } catch {
    // Session handoff is a UX convenience, never an authority boundary.
  }
}

export function createStudioFeaturedOutcomeDraft(input: {
  draftId?: string;
  principalId: string | null;
  skuId: StudioOutcomeSkuId;
  prompt: string;
  awaitingClarification?: boolean;
}): StudioFeaturedOutcomeDraft {
  const draftId = input.draftId ?? globalThis.crypto.randomUUID();
  if (!validDraftId(draftId)) {
    throw new Error("studio_featured_outcome_draft_id_invalid");
  }
  if (!validPrincipalId(input.principalId)) {
    throw new Error("studio_featured_outcome_draft_principal_invalid");
  }
  if (!validPrompt(input.prompt)) {
    throw new Error("studio_featured_outcome_draft_prompt_invalid");
  }
  const outcomeIntent = parseStudioOutcomeIntent({
    source: STUDIO_FEATURED_OUTCOME_SOURCE,
    skuId: input.skuId,
  });
  return {
    schemaVersion: STUDIO_FEATURED_OUTCOME_DRAFT_VERSION,
    outcomeIntent,
    draftId,
    principalId: input.principalId,
    prompt: input.prompt,
    awaitingClarification: input.awaitingClarification === true,
  };
}

export function readStudioFeaturedOutcomeDraft(
  principalId: string | null,
): StudioFeaturedOutcomeDraft | null {
  const storage = sessionStorageOrNull();
  if (!storage) return null;
  try {
    for (const key of LEGACY_STORAGE_KEYS) storage.removeItem(key);
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Record<string, unknown>;
    const intent = value.outcomeIntent;
    if (
      Object.keys(value).sort().join("\0") !==
        [
          "schemaVersion",
          "outcomeIntent",
          "draftId",
          "principalId",
          "prompt",
          "awaitingClarification",
        ]
          .sort()
          .join("\0") ||
      value.schemaVersion !== STUDIO_FEATURED_OUTCOME_DRAFT_VERSION ||
      !validDraftId(value.draftId) ||
      !validPrincipalId(value.principalId) ||
      value.principalId !== principalId ||
      !validPrompt(value.prompt) ||
      typeof value.awaitingClarification !== "boolean"
    ) {
      removeStoredDraft(storage);
      return null;
    }
    return {
      schemaVersion: STUDIO_FEATURED_OUTCOME_DRAFT_VERSION,
      outcomeIntent: parseStudioOutcomeIntent(intent),
      draftId: value.draftId,
      principalId: value.principalId,
      prompt: value.prompt,
      awaitingClarification: value.awaitingClarification,
    } as StudioFeaturedOutcomeDraft;
  } catch {
    removeStoredDraft(storage);
    return null;
  }
}

export function persistStudioFeaturedOutcomeDraft(
  draft: StudioFeaturedOutcomeDraft,
): boolean {
  const storage = sessionStorageOrNull();
  if (!storage) return false;
  const validated = createStudioFeaturedOutcomeDraft({
    draftId: draft.draftId,
    principalId: draft.principalId,
    skuId: draft.outcomeIntent.skuId,
    prompt: draft.prompt,
    awaitingClarification: draft.awaitingClarification,
  });
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(validated));
    return true;
  } catch {
    return false;
  }
}

export function persistStudioFeaturedOutcomeDraftIfCurrent(
  draftId: string,
  draft: StudioFeaturedOutcomeDraft,
): boolean {
  if (!validDraftId(draftId) || draft.draftId !== draftId) return false;
  const storage = sessionStorageOrNull();
  if (!storage) return false;
  const validated = createStudioFeaturedOutcomeDraft({
    draftId: draft.draftId,
    principalId: draft.principalId,
    skuId: draft.outcomeIntent.skuId,
    prompt: draft.prompt,
    awaitingClarification: draft.awaitingClarification,
  });
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const current = JSON.parse(raw) as Record<string, unknown>;
    if (current.draftId !== draftId) return false;
    storage.setItem(STORAGE_KEY, JSON.stringify(validated));
    return true;
  } catch {
    removeStoredDraft(storage);
    return false;
  }
}

export function clearStudioFeaturedOutcomeDraft(): void {
  const storage = sessionStorageOrNull();
  if (storage) removeStoredDraft(storage);
}

export function clearStudioFeaturedOutcomeDraftIfCurrent(
  draftId: string,
): boolean {
  if (!validDraftId(draftId)) return false;
  const storage = sessionStorageOrNull();
  if (!storage) return false;
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const value = JSON.parse(raw) as Record<string, unknown>;
    if (value.draftId !== draftId) return false;
    removeStoredDraft(storage);
    return true;
  } catch {
    removeStoredDraft(storage);
    return false;
  }
}

export function claimStudioGenerationRequest(requestId: string): boolean {
  if (!requestId.trim()) return false;
  if (activeGenerationRequestId === requestId) return true;
  if (activeGenerationRequestId) return false;
  activeGenerationRequestId = requestId;
  return true;
}

export function releaseStudioGenerationRequest(requestId: string): void {
  if (activeGenerationRequestId === requestId) {
    activeGenerationRequestId = null;
  }
}

export function resetStudioGenerationRequestClaimsForTests(): void {
  activeGenerationRequestId = null;
}
